import React, { useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  Face as FaceIcon,
  ChildCare as ChildCareIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface FaceResult {
  index: number;
  // Ensemble fields (preferred)
  insightface_age?: number | null;
  deepface_age?: number | null;
  ensemble_age?: number | null;
  disagreement?: number | null;
  // Backward compat: older payloads used `age` directly
  age?: number | null;
  gender: 'male' | 'female' | null;
  bbox: [number, number, number, number] | null;
  detection_score: number | null;
}

interface AgeTestResult {
  success: boolean;
  image_size: { width: number; height: number };
  face_count: number;
  min_age: number | null;
  max_age: number | null;
  faces: FaceResult[];
  deepface_available?: boolean;
}

/** Used by table/overlay — falls back gracefully across payload shapes. */
const displayAge = (f: FaceResult): number | null =>
  (f.ensemble_age ?? f.age ?? null);

const ageColor = (age: number | null, csamThreshold = 18): 'error' | 'warning' | 'success' => {
  if (age == null) return 'warning';
  if (age < csamThreshold) return 'error';
  if (age < csamThreshold + 4) return 'warning'; // young-adult / borderline
  return 'success';
};

const AgeDetectionTest: React.FC = () => {
  const [csamThreshold, setCsamThreshold] = useState<number>(18);
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<AgeTestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageDisplaySize, setImageDisplaySize] = useState<{ w: number; h: number } | null>(null);

  // Load current CSAM threshold once so the result chips reflect actual config
  React.useEffect(() => {
    apiService.getAppConfig().then((r) => {
      if (typeof r?.data?.csamAgeThreshold === 'number') {
        setCsamThreshold(r.data.csamAgeThreshold);
      }
    }).catch(() => {});
  }, []);

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setError(null);
    setPreview(URL.createObjectURL(file));
    setLoading(true);
    try {
      const r = await apiService.testAgeDetection(file);
      setResult(r.data as AgeTestResult);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Age detection failed');
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onImageLoad = () => {
    if (imageRef.current) {
      setImageDisplaySize({
        w: imageRef.current.clientWidth,
        h: imageRef.current.clientHeight,
      });
    }
  };

  // Convert original-image bbox coords to overlay coords scaled to the displayed image.
  const bboxStyle = (
    bbox: [number, number, number, number],
    age: number | null
  ): React.CSSProperties => {
    if (!result || !imageDisplaySize) return { display: 'none' };
    const scaleX = imageDisplaySize.w / result.image_size.width;
    const scaleY = imageDisplaySize.h / result.image_size.height;
    const [x1, y1, x2, y2] = bbox;
    const color = ageColor(age, csamThreshold);
    const borderColor =
      color === 'error' ? '#f44336' : color === 'warning' ? '#ff9800' : '#4caf50';
    return {
      position: 'absolute',
      left: x1 * scaleX,
      top: y1 * scaleY,
      width: (x2 - x1) * scaleX,
      height: (y2 - y1) * scaleY,
      border: `2px solid ${borderColor}`,
      borderRadius: 4,
      boxShadow: `0 0 0 1px rgba(0,0,0,0.4)`,
      pointerEvents: 'none',
    };
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <FaceIcon color="primary" />
        <Typography variant="h5">Age Detection Test</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Empirically validate the age estimator that powers CSAM detection.
        Upload any photo (your own, family photos of children fully clothed,
        public domain test photos) and see what age the system estimates for
        every face it detects. This is a read-only probe — no incident is
        logged, no image stored, no auto-block triggered.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Current CSAM age threshold: <strong>&lt; {csamThreshold} years</strong>{' '}
        triggers the child-safety escalation when paired with detected nudity.
        Bounding boxes use the <strong>ensemble age</strong> (MIN of all
        available estimators) for safety: red if below threshold, amber if
        borderline, green if clearly adult.
      </Alert>

      {result && result.deepface_available === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>DeepFace not loaded on the Python service.</strong> Age
          estimation is currently using only InsightFace's
          <code>genderage.onnx</code> model, which has known accuracy issues
          on diverse faces. Install <code>deepface</code> via the Python
          requirements (<code>pip install -r requirements.txt</code>) and
          restart the service to enable the ensemble.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              {loading ? 'Detecting…' : 'Upload a test image'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onSelectFile}
            />
            {loading && <CircularProgress size={20} />}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Use safe, age-known photos: your own selfies, fully-clothed family
            photos with children, public-domain school portraits, etc. Never
            upload illegal material.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Image with detected faces
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              <img
                ref={imageRef}
                src={preview}
                alt="Age detection target"
                onLoad={onImageLoad}
                style={{
                  maxWidth: '100%',
                  maxHeight: 520,
                  display: 'block',
                  borderRadius: 8,
                }}
              />
              {result?.faces.map((f) =>
                f.bbox ? (
                  <Box key={f.index} sx={bboxStyle(f.bbox, displayAge(f))}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -22,
                        left: 0,
                        bgcolor: 'rgba(0,0,0,0.75)',
                        color: 'white',
                        px: 0.6,
                        py: 0.1,
                        fontSize: 11,
                        borderRadius: 0.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Face #{f.index + 1}: {displayAge(f) ?? '?'}y
                    </Box>
                  </Box>
                ) : null
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography variant="subtitle1">Detection summary</Typography>
              <Chip label={`${result.face_count} face${result.face_count === 1 ? '' : 's'}`} size="small" />
              {result.min_age != null && (
                <Chip
                  label={`min ${result.min_age}y`}
                  size="small"
                  color={ageColor(result.min_age, csamThreshold)}
                />
              )}
              {result.max_age != null && result.min_age !== result.max_age && (
                <Chip label={`max ${result.max_age}y`} size="small" />
              )}
              {result.min_age != null && result.min_age < csamThreshold && (
                <Chip
                  icon={<ChildCareIcon />}
                  label="Would trigger CSAM (with nudity)"
                  color="error"
                  size="small"
                />
              )}
            </Stack>

            {result.face_count === 0 ? (
              <Alert severity="warning">
                No faces were detected. If you expected a face here, the image
                might be too small, blurry, heavily occluded, or at an extreme
                angle. The detector works best on clear, frontal, well-lit
                faces — same constraint as the face-swap pipeline.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Face</TableCell>
                    <TableCell>InsightFace</TableCell>
                    <TableCell>DeepFace</TableCell>
                    <TableCell>Ensemble (min)</TableCell>
                    <TableCell>Disagreement</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Det. conf.</TableCell>
                    <TableCell>Classification</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.faces.map((f) => {
                    const ageForClass = displayAge(f);
                    const c = ageColor(ageForClass, csamThreshold);
                    const disagreement = f.disagreement ?? null;
                    return (
                      <TableRow key={f.index}>
                        <TableCell>#{f.index + 1}</TableCell>
                        <TableCell>
                          {f.insightface_age == null ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            <Chip
                              size="small"
                              label={`${f.insightface_age}y`}
                              color={ageColor(f.insightface_age, csamThreshold)}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {f.deepface_age == null ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            <Chip
                              size="small"
                              label={`${f.deepface_age}y`}
                              color={ageColor(f.deepface_age, csamThreshold)}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {ageForClass == null ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            <Chip
                              size="small"
                              label={`${ageForClass}y`}
                              color={c}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {disagreement == null ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            <Chip
                              size="small"
                              label={`±${disagreement}y`}
                              color={disagreement > 10 ? 'warning' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>{f.gender ?? '—'}</TableCell>
                        <TableCell>
                          {f.detection_score == null
                            ? '—'
                            : `${Math.round((f.detection_score || 0) * 100)}%`}
                        </TableCell>
                        <TableCell>
                          {ageForClass == null ? (
                            <Chip label="age unknown" size="small" />
                          ) : c === 'error' ? (
                            <Chip label="MINOR" color="error" size="small" />
                          ) : c === 'warning' ? (
                            <Chip label="borderline" color="warning" size="small" />
                          ) : (
                            <Chip label="adult" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <Divider sx={{ my: 2 }} />
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>How to read this:</strong>
                <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
                  <li>
                    Two independent age models run on every face. The
                    <strong> Ensemble (min)</strong> column is the value used
                    by the CSAM pipeline — taking the MIN biases toward
                    flagging, which is the correct direction for child safety.
                  </li>
                  <li>
                    <strong>InsightFace</strong> is the small built-in model
                    (~1.4 MB, &lt;1 ms). Fast but unreliable on diverse faces.
                  </li>
                  <li>
                    <strong>DeepFace</strong> is the heavier age model (~553 MB
                    VGG-Face backbone, ~50–100 ms). Far more accurate on
                    diverse demographics; this is what stops minors from
                    silently slipping past as "30+".
                  </li>
                  <li>
                    Big <strong>disagreement</strong> (&gt;10y) means the
                    models can't agree — usually because the face is partially
                    occluded, at an unusual angle, or out-of-distribution for
                    one of the models. The MIN value is still used.
                  </li>
                  <li>
                    To tighten enforcement: lower the threshold (e.g. 16).
                    To catch more borderline cases: raise it (e.g. 21).
                  </li>
                </ul>
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AgeDetectionTest;
